import { z } from 'zod';
import { AssignableDocumentCategorySchema, DocumentCategorySchema } from './documents.schemas';
import { ReadinessObligationSchema } from './readiness.schemas';
import { refersTo } from '../common/refers-to';

/**
 * Owner document request (Dokumentenanforderung) — IMM-383.2. A record that the
 * agent raised a request for missing documents from the Objekt's Eigentümer. The
 * e-mail itself (compose/brand/send) is IMM-412; this contract only creates the
 * record and hands it off.
 */

export const CreateDocumentRequestBodySchema = z.object({
  // The IMM-218 Kategorien to request; the FE sends the missing ones (single row
  // or every gap for the bulk action). The obligation is derived server-side.
  categories: z.array(AssignableDocumentCategorySchema).min(1),
  // Optional agent note carried into the e-mail by IMM-412.
  note: z.string().trim().max(500).nullish(),
});
export type CreateDocumentRequestBody = z.infer<typeof CreateDocumentRequestBodySchema>;

export const DocumentRequestCategorySchema = z.object({
  category: DocumentCategorySchema,
  // Pflichtstufe snapshotted at request time (drives the e-mail grouping in IMM-412).
  obligation: ReadinessObligationSchema,
});
export type DocumentRequestCategory = z.infer<typeof DocumentRequestCategorySchema>;

export const DocumentRequestResponseSchema = z.object({
  id: z.uuid(),
  propertyId: refersTo('property', z.uuid()),
  recipientContactId: refersTo('contact', z.uuid()),
  // The Eigentümer's display name, resolved when the request was raised. Carried
  // so the confirmation can name who was written to („Anforderung an {name}
  // gesendet.") — the id alone forces the caller to say „den Eigentümer", which
  // is what shipped and what IMM-479 reported. Nullable because a contact may
  // legitimately have no display name; the caller falls back on null.
  recipientName: z.string().nullable(),
  categories: z.array(DocumentRequestCategorySchema),
  note: z.string().nullable(),
  requestedByMemberId: refersTo('member', z.uuid()),
  requestedAt: z.string(),
});
export type DocumentRequestResponse = z.infer<typeof DocumentRequestResponseSchema>;
